import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import pb from '../../lib/pocketbase'; // Import de PocketBase
import './SoldItemDetail.css';
import { useAuth } from '../../context/AuthContext';
import Select from 'react-select';
import imageCompression from 'browser-image-compression';

import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

function SoldItemDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [item, setItem] = useState(null);
  const [similarItems, setSimilarItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeImage, setActiveImage] = useState('');
  const [openLightbox, setOpenLightbox] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);

  // États pour l'édition admin
  const [isEditing, setIsEditing] = useState(false);
  const [editedItem, setEditedItem] = useState({});
  const [categories, setCategories] = useState([]);
  const [colors, setColors] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [pearlTypes, setPearlTypes] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchSoldItem = async () => {
      setLoading(true);
      setError(null);
      try {
        // Récupérer les données de référence pour l'édition
        const [currentItem, categoriesData, colorsData, materialsData, pearlTypesData] = await Promise.all([
          pb.collection('products').getFirstListItem(`slug = "${slug}"`, {
            expand: 'category,color,material,pearl_type',
            requestKey: null
          }),
          pb.collection('categories').getFullList({ requestKey: null }),
          pb.collection('colors').getFullList({ requestKey: null }),
          pb.collection('materials').getFullList({ requestKey: null }),
          pb.collection('pearl_types').getFullList({ requestKey: null })
        ]);

        if (!isMounted) return;

        setItem(currentItem);
        setCategories(categoriesData);
        setColors(colorsData);
        setMaterials(materialsData);
        setPearlTypes(pearlTypesData);

        if (currentItem.image && currentItem.image.length > 0) {
          const imageUrl = pb.files.getURL(currentItem, currentItem.image[0]);
          setActiveImage(imageUrl);
        }

        if (currentItem.expand.category && currentItem.expand.category.length > 0) {
          const categoryFilters = currentItem.expand.category.map(cat => `category.id = "${cat.id}"`).join(' || ');
          const resultList = await pb.collection('products').getList(1, 5, {
            filter: `(${categoryFilters}) && id != "${currentItem.id}"`,
            sort: '-created',
            requestKey: null
          });
          if (isMounted) {
            setSimilarItems(resultList.items);
          }
        }

      } catch (err) {
        if (err.name === 'AbortError' || err.isAbort) {
          return; // Ignore silently
        }
        console.error("Erreur détaillée lors de la récupération de l'article vendu:", err);
        if (isMounted) {
            setError(`La création avec le slug "${slug}" n'a pas été trouvée.`);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchSoldItem();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  if (loading) {
    return <div className="loading-message">Chargement de la création...</div>;
  }

  if (error) {
    return (
      <div className="product-not-found">
        <h2>Oups ! {error}</h2>
        <Link to="/galerie">Retour à la galerie</Link>
      </div>
    );
  }

  if (!item) {
    return null; // Should not happen if loading/error are handled
  }

  const handleImageClick = (index) => {
    setImageIndex(index);
    setOpenLightbox(true);
  };

  // Utiliser les images mises à jour en temps réel pendant l'édition
  const currentImages = isEditing ? (item.image || []) : (item.image || []);
  const itemImages = currentImages.map(img => ({ src: pb.files.getURL(item, img) }));

  // Vérifier si l'utilisateur est admin
  const isAdmin = user && user.role === 'admin';

  // Fonctions d'édition pour les admins
  const startEditing = () => {
    setIsEditing(true);
    setEditedItem({
      name: item.name,
      price: item.price,
      description: item.description,
      stock: item.stock,
      is_unique: item.is_unique,
      is_sold: item.is_sold,
      category: item.category || [],
      color: item.color || [],
      material: item.material || [],
      pearl_type: item.pearl_type || []
    });
    setNewImages([]);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditedItem(null);
    setNewImages([]);
  };

  const saveItem = async () => {
    try {
      // Utiliser la même logique que ProductDetail avec FormData
      const formData = new FormData();
      
      // Ajouter les métadonnées
      formData.append('name', editedItem.name);
      formData.append('description', editedItem.description);
      formData.append('price', String(editedItem.price || 0));
      formData.append('stock', String(editedItem.is_unique ? 1 : (parseInt(editedItem.stock) || 0)));
      formData.append('is_unique', String(!!editedItem.is_unique));
      formData.append('is_sold', String(!!editedItem.is_sold));
      
      // Ajouter les relations (arrays)
      if (editedItem.category) editedItem.category.forEach(id => formData.append('category', id));
      if (editedItem.color) editedItem.color.forEach(id => formData.append('color', id));
      if (editedItem.material) editedItem.material.forEach(id => formData.append('material', id));
      if (editedItem.pearl_type) editedItem.pearl_type.forEach(id => formData.append('pearl_type', id));

      // Ajouter les images existantes (celles qui n'ont pas été supprimées)
      const currentImages = item.image || [];
      if (currentImages.length > 0) {
        currentImages.forEach(imgName => formData.append('image', imgName));
      } else {
        formData.append('image', ''); // Vide le champ image si aucune image existante
      }

      // Première mise à jour avec les métadonnées et images existantes
      let updatedRecord = await pb.collection('products').update(item.id, formData);

      // Si il y a de nouvelles images, les ajouter aux images existantes
      if (newImages.length > 0) {
        const addImagesFormData = new FormData();
        
        // D'abord, récupérer le produit mis à jour pour avoir les images actuelles
        const currentProduct = await pb.collection('products').getOne(item.id);
        const existingImages = currentProduct.image || [];
        
        // Ajouter toutes les images existantes
        existingImages.forEach(imgName => addImagesFormData.append('image', imgName));
        
        // Ajouter les nouvelles images
        newImages.forEach(file => addImagesFormData.append('image', file));
        
        updatedRecord = await pb.collection('products').update(item.id, addImagesFormData);
      }
      
      // Recharger le produit avec les relations
      const refreshedItem = await pb.collection('products').getOne(item.id, {
        expand: 'category,color,material,pearl_type'
      });
      
      setItem(refreshedItem);
      setIsEditing(false);
      setEditedItem(null);
      setNewImages([]);
      alert('Produit mis à jour avec succès !');
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert('Erreur lors de la mise à jour du produit');
    }
  };

  const handleInputChange = (field, value) => {
    setEditedItem(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Fonctions pour la gestion des images
  const handleFileChange = async (files) => {
    const fileArray = Array.from(files);
    
    // Compression des images
    const compressedFiles = await Promise.all(fileArray.map(async (file) => {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        initialQuality: 0.8
      };
      try {
        if (file.size / 1024 / 1024 > options.maxSizeMB) {
          const compressedFile = await imageCompression(file, options);
          return compressedFile;
        } else {
          return file;
        }
      } catch (error) {
        console.error('Erreur de compression:', error);
        return file;
      }
    }));

    setNewImages(prev => [...prev, ...compressedFiles]);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileChange(e.dataTransfer.files);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDeleteNewImage = (filenameToDelete) => {
    setNewImages(prev => prev.filter(file => file.name !== filenameToDelete));
  };

  const handleDeleteExistingImage = (filenameToDelete) => {
    setItem(prev => {
      const updatedImages = prev.image.filter(img => img !== filenameToDelete);
      
      // Mettre à jour l'image active si l'image supprimée était l'image active
      if (updatedImages.length > 0) {
        const newActiveImageUrl = pb.files.getURL(prev, updatedImages[0]);
        setActiveImage(newActiveImageUrl);
      }
      
      return {
        ...prev,
        image: updatedImages
      };
    });
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const existingImages = item.image || [];
    const allImages = [...existingImages, ...newImages];
    
    // Réorganiser les images
    const draggedImage = allImages[draggedIndex];
    const newAllImages = [...allImages];
    newAllImages.splice(draggedIndex, 1);
    newAllImages.splice(dropIndex, 0, draggedImage);

    // Séparer les images existantes et nouvelles
    const newExistingImages = [];
    const newNewImages = [];

    newAllImages.forEach(img => {
      if (typeof img === 'string') {
        newExistingImages.push(img);
      } else {
        newNewImages.push(img);
      }
    });

    setItem(prev => ({
      ...prev,
      image: newExistingImages
    }));
    setNewImages(newNewImages);
    
    // Mettre à jour l'image active si nécessaire
    if (newExistingImages.length > 0) {
      const newActiveImageUrl = pb.files.getURL(item, newExistingImages[0]);
      setActiveImage(newActiveImageUrl);
    }
    
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleSelectChange = (selectedOptions, actionMeta) => {
    const fieldName = actionMeta.name;
    const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setEditedItem(prev => ({
      ...prev,
      [fieldName]: values
    }));
  };

  return (
    <>
      <div className="sold-item-detail-page">
        <button onClick={() => navigate(-1)} className="back-btn">← Retour</button>
        <div className="sold-item-detail-container">
          <div className="product-gallery">
            {isEditing ? (
              <div className="image-management-section">
                <h3>Gestion des images</h3>
                
                {/* Zone de drop pour nouvelles images */}
                <div 
                  className={`image-drop-zone ${isDragging ? 'dragging' : ''}`}
                  onDrop={handleFileDrop}
                  onDragOver={handleDrag}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                >
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => handleFileChange(e.target.files)}
                    className="file-input"
                    id="image-upload"
                  />
                  <label htmlFor="image-upload" className="drop-label">
                    <svg viewBox="0 0 24 24" className="upload-icon">
                      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                    </svg>
                    <span>Glissez vos images ici ou cliquez pour sélectionner</span>
                  </label>
                </div>

                {/* Images existantes et nouvelles */}
                <div className="images-grid">
                  {/* Images existantes */}
                  {item.image && item.image.map((img, index) => {
                    const allImagesIndex = index;
                    return (
                      <div 
                        key={`existing-${index}`} 
                        className={`image-item existing-image ${draggedIndex === allImagesIndex ? 'dragging' : ''} ${dragOverIndex === allImagesIndex ? 'drag-over' : ''}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, allImagesIndex)}
                        onDragOver={(e) => handleDragOver(e, allImagesIndex)}
                        onDragEnd={handleDragEnd}
                        onDrop={(e) => handleDrop(e, allImagesIndex)}
                      >
                        <img src={pb.files.getURL(item, img)} alt={`Image ${index + 1}`} />
                        <button 
                          type="button" 
                          onClick={() => handleDeleteExistingImage(img)}
                          className="delete-image-btn"
                        >
                          ×
                        </button>
                        <span className="image-order">{index + 1}</span>
                      </div>
                    );
                  })}
                  
                  {/* Nouvelles images */}
                  {newImages.map((file, index) => {
                    const allImagesIndex = (item.image?.length || 0) + index;
                    return (
                      <div 
                        key={`new-${index}`} 
                        className={`image-item new-image ${draggedIndex === allImagesIndex ? 'dragging' : ''} ${dragOverIndex === allImagesIndex ? 'drag-over' : ''}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, allImagesIndex)}
                        onDragOver={(e) => handleDragOver(e, allImagesIndex)}
                        onDragEnd={handleDragEnd}
                        onDrop={(e) => handleDrop(e, allImagesIndex)}
                      >
                        <img src={URL.createObjectURL(file)} alt={`Nouvelle image ${index + 1}`} />
                        <button 
                          type="button" 
                          onClick={() => handleDeleteNewImage(file.name)}
                          className="delete-image-btn"
                        >
                          ×
                        </button>
                        <span className="image-order">{allImagesIndex + 1}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <>
                <div className="main-image-container" onClick={() => handleImageClick(itemImages.findIndex(img => img.src === activeImage))}>
                  <img src={activeImage} alt={item.name} className="main-image" />
                  <div className="zoom-overlay">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                    </svg>
                  </div>
                </div>
                <div className="thumbnail-gallery">
                  {itemImages.map((image, index) => (
                    <button key={index} className={`thumbnail-btn ${image.src === activeImage ? 'active' : ''}`} onClick={() => setActiveImage(image.src)}>
                      <img src={image.src} alt={`Miniature ${item.name} ${index + 1}`} className="thumbnail-image" />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="sold-item-detail-info">
            {isEditing ? (
              <input 
                type="text" 
                value={editedItem.name} 
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="edit-input edit-title"
              />
            ) : (
              <h1>{item.name}</h1>
            )}
            {isEditing ? (
              <input 
                type="number" 
                value={editedItem.price} 
                onChange={(e) => handleInputChange('price', parseFloat(e.target.value))}
                className="edit-input edit-price"
                step="0.01"
              />
            ) : (
              item.price ? <p className="price">{item.price} €</p> : null
            )}
            
            {isEditing ? (
              <div className="product-stock-detail">
                <div className="edit-stock-controls">
                  <label>
                    <input 
                      type="checkbox" 
                      checked={editedItem.is_unique}
                      onChange={(e) => handleInputChange('is_unique', e.target.checked)}
                    />
                    Pièce unique
                  </label>
                  <label>
                    <input 
                      type="checkbox" 
                      checked={editedItem.is_sold}
                      onChange={(e) => handleInputChange('is_sold', e.target.checked)}
                    />
                    Vendu
                  </label>
                  {!editedItem.is_unique && (
                    <label>
                      Stock: 
                      <input 
                        type="number" 
                        value={editedItem.stock} 
                        onChange={(e) => handleInputChange('stock', parseInt(e.target.value))}
                        className="edit-input edit-stock"
                        min="0"
                      />
                    </label>
                  )}
                </div>
              </div>
            ) : (
              item.is_sold && (
                <p className="sold-badge-detail">
                  {item.is_unique ? 'Pièce Unique - Vendue' : 'Vendue'}
                </p>
              )
            )}
            
            {isEditing ? (
              <textarea 
                value={editedItem.description} 
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="edit-input edit-description"
                rows="4"
              />
            ) : (
              <p className="description">{item.description}</p>
            )}
            
            {isEditing ? (
              <div className="edit-selects-container">
                <div className="edit-select-group">
                  <label>Catégories</label>
                  <Select
                    name="category"
                    options={categories.map(c => ({ value: c.id, label: c.name }))}
                    isMulti
                    value={categories.filter(c => editedItem.category.includes(c.id)).map(c => ({ value: c.id, label: c.name }))}
                    onChange={handleSelectChange}
                    placeholder="Sélectionner des catégories"
                    className="edit-select"
                  />
                </div>
                
                <div className="edit-select-group">
                  <label>Couleurs</label>
                  <Select
                    name="color"
                    options={colors.map(c => ({ value: c.id, label: c.name }))}
                    isMulti
                    value={colors.filter(c => editedItem.color.includes(c.id)).map(c => ({ value: c.id, label: c.name }))}
                    onChange={handleSelectChange}
                    placeholder="Sélectionner des couleurs"
                    className="edit-select"
                  />
                </div>
                
                <div className="edit-select-group">
                  <label>Matériaux</label>
                  <Select
                    name="material"
                    options={materials.map(m => ({ value: m.id, label: m.name }))}
                    isMulti
                    value={materials.filter(m => editedItem.material.includes(m.id)).map(m => ({ value: m.id, label: m.name }))}
                    onChange={handleSelectChange}
                    placeholder="Sélectionner des matériaux"
                    className="edit-select"
                  />
                </div>
                
                <div className="edit-select-group">
                  <label>Types de perles</label>
                  <Select
                    name="pearl_type"
                    options={pearlTypes.map(p => ({ value: p.id, label: p.name }))}
                    isMulti
                    value={pearlTypes.filter(p => editedItem.pearl_type.includes(p.id)).map(p => ({ value: p.id, label: p.name }))}
                    onChange={handleSelectChange}
                    placeholder="Sélectionner des types de perles"
                    className="edit-select"
                  />
                </div>
              </div>
            ) : (
              <div className="product-meta">
                {item.expand?.category && <span><strong>Catégorie:</strong> {item.expand.category.map(c => c.name).join(', ')}</span>}
                {item.expand?.material && <span><strong>Matière:</strong> {item.expand.material.map(m => m.name).join(', ')}</span>}
                {item.expand?.pearl_type && <span><strong>Perle:</strong> {item.expand.pearl_type.map(p => p.name).join(', ')}</span>}
                {item.expand?.color && <span><strong>Couleur:</strong> {item.expand.color.map(c => c.name).join(', ')}</span>}
              </div>
            )}
            
            {/* Boutons d'édition admin */}
            {isAdmin && (
              <div className="admin-controls-repositioned">
                {!isEditing ? (
                  <button className="edit-btn" onClick={startEditing}>
                    ✏️ Modifier le produit
                  </button>
                ) : (
                  <div className="edit-controls">
                    <button className="save-btn" onClick={saveItem}>
                      ✅ Sauvegarder
                    </button>
                    <button className="cancel-btn" onClick={cancelEditing}>
                      ❌ Annuler
                    </button>
                  </div>
                )}
              </div>
            )}
            
            {!isEditing && <Link to="/catalogue" className="cta-button">Voir les créations disponibles</Link>}
          </div>
        </div>
      </div>

      {similarItems.length > 0 && (
        <section className="similar-items">
          <h2>Vous aimerez aussi...</h2>
          <div className="slider-container">
            <div className="sold-slider">
              {/* Dupliquer les items pour un défilement infini */}
              {[...similarItems, ...similarItems].map((similarItem, index) => {
                const similarProductImage = similarItem.image && similarItem.image.length > 0 ? pb.files.getURL(similarItem, similarItem.image[0]) : '/placeholder.jpg';
                const linkTo = similarItem.is_sold ? `/galerie/vendu/${similarItem.slug}` : `/catalogue/produit/${similarItem.slug}`;
                return (
                  <Link to={linkTo} key={`${similarItem.id}-${index}`} className="sold-card-link slider-item">
                    <div className="sold-card">
                      <div className="sold-image-wrapper">
                        <img src={similarProductImage} alt={similarItem.name} className="sold-image" />
                        {similarItem.is_sold && <span className="sold-badge">Vendu</span>}
                      </div>
                      <div className="sold-info">
                        <h3>{similarItem.name}</h3>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <Lightbox
        open={openLightbox}
        close={() => setOpenLightbox(false)}
        slides={itemImages}
        index={imageIndex}
      />
    </>
  );
}

export default SoldItemDetail;